import { cloneDeep } from 'lodash';
import { useMemo, useReducer, useState } from 'react';
import { match } from 'ts-pattern';
import { JsonLocalStorageService } from '../../../api/local-storage-service';
import { TimeRange } from '../../../api/timeranges/types';
import { Domains } from '../../../constants';
import {
  useLatestPreferencesContext,
  useOldestStartDateContext,
} from '../../../context/contexts';
import { reducerWithPersistentWrapper } from '../../../context/hooks';
import { Timestamp } from '../../../types';
import { getMomentDate } from '../../../utils-date';
import {
  CalendarYearMonthlyTimesliderGranularity,
  CalendarYearYearlyTimesliderGranularity,
  FinancialYearQuarterlyTimesliderGranularity,
  FinancialYearYearlyTimesliderGranularity,
} from './constants';
import {
  Granularity,
  MenuHandle,
  TimeSliderAction,
  TimeSliderGranularity,
  TimeSliderGranularityAction,
  TimeSliderPresetAction,
  TimeSliderRangeUpdateAction,
  TimeSliderState,
} from './types';
import {
  GranularityDefaultInfo,
  jsonParserReviver,
  presetToTimeRange,
} from './utils';

export const timeSliderGranularities: TimeSliderGranularity[] = [
  CalendarYearMonthlyTimesliderGranularity,
  CalendarYearYearlyTimesliderGranularity,
  FinancialYearYearlyTimesliderGranularity,
  FinancialYearQuarterlyTimesliderGranularity,
];

// TODO: should be based on client settings
export const getInitialRangeTimeSliderState = (
  availableTimeRanges: Record<Granularity, TimeRange>,
  current: Timestamp
): TimeSliderState => {
  const selectedGranularity = CalendarYearMonthlyTimesliderGranularity;
  const range = availableTimeRanges[selectedGranularity.value];
  const granularityInfo = GranularityDefaultInfo(availableTimeRanges, current);
  const selectedPreset =
    granularityInfo[selectedGranularity.value].defaultPreset;
  const { start, end } = presetToTimeRange(
    selectedPreset,
    range,
    selectedGranularity
  );
  const initialState = {
    start,
    end,
    range,
    initialRange: { ...range },
    granularities: timeSliderGranularities,
    selectedGranularity,
    selectedPreset,
    availableTimeRanges,
    granularityInfo,
  };
  return {
    ...initialState,
    initialState: cloneDeep(initialState),
  };
};

export const useTimeSliderWithPersistence = (
  domain: Domains,
  init: TimeSliderState
) => {
  const persistenceKey = `${domain}-timeslider-v1`;
  const localStorageService = new JsonLocalStorageService<TimeSliderState>(
    persistenceKey,
    jsonParserReviver
  );
  const storedItems: TimeSliderState | null = localStorageService.get();
  const state = storedItems ?? { ...init };
  return useReducer(
    reducerWithPersistentWrapper(reducer, localStorageService),
    state
  );
};

export const useTimeSlider = (init: TimeSliderState) => {
  return useReducer(reducer, init);
};

export const reducer = (
  state: TimeSliderState,
  action: TimeSliderAction
): TimeSliderState => {
  return match(action)
    .with({ type: 'reset' }, () => {
      const newState = state.initialState;
      return { ...newState, initialState: newState };
    })
    .with({ type: 'update-range' }, (a: TimeSliderRangeUpdateAction) => {
      return {
        ...state,
        start: a.start,
        end: a.end,
      };
    })
    .with({ type: 'update-granularity' }, (a: TimeSliderGranularityAction) => {
      const selectedGranularity = a.granularity;
      const range = state.availableTimeRanges[selectedGranularity.value];
      const granularityInfo = GranularityDefaultInfo(
        state.availableTimeRanges,
        a.current
      )[selectedGranularity.value];
      const selectedPreset = granularityInfo.defaultPreset;
      const { start, end } = presetToTimeRange(
        selectedPreset,
        range,
        selectedGranularity
      );
      const newState = {
        ...state,
        selectedGranularity,
        selectedPreset,
        range,
        start,
        end,
      };
      return newState;
    })
    .with({ type: 'update-preset' }, (a: TimeSliderPresetAction) => {
      const selectedGranularity = a.granularity;
      const range = state.availableTimeRanges[selectedGranularity.value];
      const selectedPreset = a.preset;
      const { start, end } = presetToTimeRange(
        selectedPreset,
        range,
        selectedGranularity
      );
      const newState = {
        ...state,
        selectedGranularity,
        selectedPreset,
        range,
        start,
        end,
      };
      return newState;
    })
    .exhaustive();
};

export const useMenu = (): MenuHandle => {
  const [opened, setOpened] = useState(false);
  const [anchorEl, setAnchorEl] = useState<ParentNode | null>(null);
  return {
    opened,
    open: () => setOpened(true),
    close: () => setOpened(false),
    anchorEl,
    setAnchorEl,
  };
};

const FIVE_YEARS_IN_MONTHS = 60;

export const useTimeSliderRangeStartDate = (): string => {
  const latestPreferences = useLatestPreferencesContext();
  const configuredTimeSliderRangeStart: string | null =
    latestPreferences.timeSliderRangeStart ?? null;
  const oldestStartDate: string = useOldestStartDateContext();

  return useMemo(() => {
    // Calculate date from 5 years ago
    const fiveYearsAgo = getMomentDate()
      .subtract(5, 'years')
      .endOf('month')
      .format('YYYY-MM-DD');

    let effectiveStartDate = oldestStartDate;

    // If configured start date exists, use it
    if (configuredTimeSliderRangeStart) {
      effectiveStartDate = configuredTimeSliderRangeStart;
    }
    // If oldest date is more than 5 years ago, limit to 5 years
    else if (
      getMomentDate().diff(getMomentDate(oldestStartDate), 'months') >
      FIVE_YEARS_IN_MONTHS
    ) {
      effectiveStartDate = fiveYearsAgo;
    }

    // Return the latest date between effective start date and oldest start date
    return getMomentDate(effectiveStartDate).isAfter(
      getMomentDate(oldestStartDate)
    )
      ? effectiveStartDate
      : oldestStartDate;
  }, [configuredTimeSliderRangeStart, oldestStartDate]);
};
